import { BrowserModule } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { AppComponent } from './app.component';
import { PublicModule } from './public/public.module';
import { PrivateModule } from './private/private.module';
import { ServerApiService } from './server-api/server-api.service';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { provideAppInitializer, inject, NgModule } from '@angular/core';
import { TranslateModule } from './services/translate/translate.module';
import { FormsModule } from '@angular/forms';
import { registerLocaleData, APP_BASE_HREF, PlatformLocation, LocationStrategy } from '@angular/common';
import localePt from '@angular/common/locales/pt';
import localeEn from '@angular/common/locales/en';
import localeEs from '@angular/common/locales/es';
import { AppRoutingModule } from './app-routing.module';
import { AuthInterceptor } from './interceptors/http-auhorization-error.interceptor';
import { NoCacheHttpInterceptor } from './interceptors/http-nocache-headers-interceptor';
import { SessionExpirationTimeoutService } from './services/authorization/session-expiration-timeout.service';
import { ConfigurationsService } from './services/configurations/configurations.service';
import { RequiredActionModule } from './required-action/required-action.module';
import { CookieService } from 'ngx-cookie-service';
import { CustomLocationStrategy } from './custom-location-strategy';
import { AuditApiService } from './server-api/audit-api.service';
import { GlobalizationApiService } from './server-api/globalization-api.service';
import { MfaApiService } from './server-api/mfa-api.service';
import { LoginApiService } from './server-api/login-api.service';
import { UserApiService } from './server-api/user-api/user-api.service';
import { ConfirmingPaymentsApiService } from './server-api/confirming-payments-api/confirming-payments-api.service';
import { CompanyApiService } from './server-api/company-api/company-api.service';
import { InvitationsApiService } from './server-api/invitations-api/invitations-api.service';
import { FactoringContractAddBuyerApiService } from './server-api/factoring-contract-add-buyer-api.service';
import { RouteAuthorizationService } from './services/authorization/route-authorization.service';
import { DocumentationApiService } from './server-api/documentation-api/documentation-api.service';
import { CodeListAuditApiService } from './server-api/code-list-audit-api/code-list-audit-api.service';
import { ContractConfirmingApiService } from './server-api/contract-confirming-api/contract-confirming-api.service';
import { FaqManagementApiService } from './server-api/faq-management/faq-management.service';
import { PaymentDaysSetupApiService } from './server-api/payment-days-setup/payment-days-setup-api.service';
import { MonitoringApiService } from './server-api/monitoring-api/monitoring-api.service';
import { SchedulerApiService } from './server-api/scheduler-api/scheduler-api.service';
registerLocaleData(localePt, 'pt');
registerLocaleData(localeEn, 'en');
registerLocaleData(localeEs, 'es');

export function getBaseHref(): string {
	let defaultBaseRef = '/';
	window['base-href'] = '/private/';
	return defaultBaseRef;
}

@NgModule({
	declarations: [AppComponent],
	bootstrap: [AppComponent],
	imports: [
		BrowserModule,
		NgxChartsModule,
		FormsModule,
		TranslateModule,
		PublicModule,
		RequiredActionModule,
		PrivateModule,
		AppRoutingModule,
	],
	providers: [
		// TODO(v23-migration): provideAnimationsAsync() is deprecated in Angular 20.2 in favour of
		// the new `animate.enter` / `animate.leave` template-binding API (no engine needed). This
		// project uses the old @angular/animations DSL heavily; migrate per component before v23.
		// eslint-disable-next-line @typescript-eslint/no-deprecated
		provideAnimationsAsync(),
		CookieService,
		SessionExpirationTimeoutService,
		RouteAuthorizationService,
		LoginApiService,
		MfaApiService,
		AuditApiService,
		GlobalizationApiService,
		UserApiService,
		ServerApiService,
		ConfirmingPaymentsApiService,
		CompanyApiService,
		InvitationsApiService,
		DocumentationApiService,
		FactoringContractAddBuyerApiService,
		CodeListAuditApiService,
		ContractConfirmingApiService,
		FaqManagementApiService,
		PaymentDaysSetupApiService,
		MonitoringApiService,
		SchedulerApiService,
		{
			provide: HTTP_INTERCEPTORS,
			useClass: AuthInterceptor,
			multi: true,
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: NoCacheHttpInterceptor,
			multi: true,
		},
		provideAppInitializer(() => {
			return inject(ConfigurationsService).initConfigurations();
		}),
		{
			provide: APP_BASE_HREF,
			useFactory: getBaseHref,
			deps: [PlatformLocation],
		},
		{ provide: LocationStrategy, useClass: CustomLocationStrategy },
		provideHttpClient(withInterceptorsFromDi()),
	],
})
export class AppModule {}
